import { useEffect, useState } from 'react';
import AppLayout from '../components/Layout/AppLayout';
import { api } from '../services/api';
import type { DocumentItem } from '@vedjegy/shared';

const MIME_ICON: Record<string, string> = {
  'application/pdf': '📄',
  'application/msword': '📝',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📝',
  'application/vnd.ms-excel': '📊',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📊',
  'application/vnd.ms-powerpoint': '📊',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '📊',
};

function fileIcon(mime: string | null) {
  if (!mime) return '📎';
  return MIME_ICON[mime] ?? (mime.startsWith('image/') ? '🖼️' : '📎');
}

const fmtSize = (b: number | null) =>
  b == null ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;

export default function DocumentsPage() {
  const [docs, setDocs]     = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    api.config.listDocuments()
      .then((r) => setDocs(r.documents))
      .catch(() => setError('Nem sikerült betölteni a dokumentumokat.'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppLayout>
      <div style={{ padding: '28px 40px', maxWidth: 2000 }}>
        <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>
          Dokumentumtár
        </h1>
        <p style={{ margin: '0 0 28px', fontSize: 13, color: 'var(--muted-foreground)' }}>
          Útmutatók, minta pályázatok és egyéb letölthető anyagok.
        </p>

        {loading ? (
          <p style={{ color: 'var(--muted-foreground)' }}>Betöltés...</p>
        ) : error ? (
          <div className="alert alert-destructive"><span>⚠</span><div><p>{error}</p></div></div>
        ) : docs.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--muted-foreground)', fontSize: 14 }}>
            Jelenleg nincs elérhető dokumentum.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {docs.map((doc) => (
              <div
                key={doc.id}
                style={{
                  background: '#fff', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)', padding: '14px 20px',
                  display: 'flex', alignItems: 'center', gap: 16,
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                <span style={{ fontSize: 24, lineHeight: 1, flexShrink: 0 }}>
                  {fileIcon(doc.mimeType)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {doc.displayName}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>
                    {fmtSize(doc.fileSize)}{doc.fileSize ? ' · ' : ''}{new Date(doc.createdAt).toLocaleDateString('hu-HU')}
                  </div>
                </div>
                {doc.downloadUrl ? (
                  <a
                    href={doc.downloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary btn-sm"
                    style={{ fontSize: 12, flexShrink: 0 }}
                  >
                    ↓ Letöltés
                  </a>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>—</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
